import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { BoostedDepositsTable } from '@/shared/components/boost/BoostedDepositsTable';
import { LpBoosterTable } from '@/shared/components/boost/LpBoosterTable';
import { PoolActivityTable } from '@/shared/components/boost/PoolActivityTable';
import { PositionsTable } from '@/shared/components/boost/PositionsTable';
import { TabPanel } from '@/shared/components/molecules/TabPanel';
import useBoostBalances from '@/shared/hooks/useBoostBalances';
import { useBoostPool } from '@/shared/hooks/useBoostPool';

/**
 * This table has 3 different views:
 *
 * 1. Boost pool page in the block explorer and LP app
 *    here the asset and fee tier are provided and we show all the data for the
 *    given pool
 *
 * 2. LP page in the block explorer
 *    here only the account id is provided and we show the LP's activity across
 *    all boost pools. The "LP boosters" tab is hidden in this case
 *
 * 3. My position tab of the boost pool page in the LP app
 *    here the asset, fee tier, and account id are provided to show the LP's
 *    activity for the given pool. The "LP boosters" tab is hidden in this case
 */
export default function BoostPoolTabsAndTable({
  asset,
  feeBps,
  accountId,
  queryParamName = 'table',
  blockExplorerUrl,
  highlightedAccountId,
}: {
  asset?: ChainflipAsset;
  feeBps?: number;
  accountId?: string;
  queryParamName?: string;
  blockExplorerUrl?: string;
  highlightedAccountId?: string;
}) {
  const {
    boostBalances,
    boostSharesAggregates,
    isLoading: isBoostPoolBalancesLoading,
  } = useBoostPool(asset, feeBps);

  const { data: accountBoostBalances = [], isLoading: accountBoostBalancesLoading } =
    useBoostBalances({ account: accountId });

  return (
    <TabPanel
      queryParamName={queryParamName}
      nodes={[
        ...(accountId
          ? []
          : [
              {
                name: 'LP Boosters',
                content: (
                  <LpBoosterTable
                    boostBalances={boostBalances}
                    boostShares={boostSharesAggregates}
                    isLoading={isBoostPoolBalancesLoading}
                    blockExplorerUrl={blockExplorerUrl}
                    highlightedAccountId={highlightedAccountId}
                  />
                ),
                count: boostBalances?.length,
              },
            ]),
        ...(accountId && !feeBps && !asset
          ? [
              {
                name: 'Boost Positions',
                content: (
                  <PositionsTable
                    boostBalances={accountBoostBalances}
                    boostBalancesLoading={accountBoostBalancesLoading}
                    readonly
                    accountIdSs58={accountId}
                  />
                ),
                count: accountBoostBalances?.length,
              },
            ]
          : []),
        {
          name: 'Boosted Deposits',
          content: (
            <BoostedDepositsTable
              asset={asset}
              feeBps={feeBps}
              accountId={accountId}
              blockExplorerUrl={blockExplorerUrl}
            />
          ),
        },
        {
          name: 'Pool Activity',
          content: (
            <PoolActivityTable
              asset={asset}
              feeBps={feeBps}
              accountId={accountId}
              blockExplorerUrl={blockExplorerUrl}
            />
          ),
        },
      ]}
    />
  );
}
